"use client";

import ArtworkManagement from "@/components/artwork/ArtworkManagement";
import TemplateManagement from "@/components/artwork/TemplateManagement";
import { Layer, Template } from "@/interfaces/template.interface";
import { Dialog } from "primereact/dialog";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Artwork } from "@/interfaces/artwork.interface";
import { CropSettings } from "@/interfaces/crop-settings";
import CropSettingsManagement from "@/components/artwork/CropSettingsManagement";
import LayerManagement from "@/components/artwork/LayerManagement";

enum SelectionType {
  artwork = "artwork",
  template = "template",
  layer = "layer",
}

const Header: any = {
  artwork: "Select artwork",
  template: "Select templates",
  layer: "Select layers",
};

interface Props {
  selectTemplates: () => Promise<Template[] | undefined>;
  selectLayers: () => Promise<Layer[] | undefined>;
  selectCropSettings: () => Promise<CropSettings | undefined>;
}

const ArtworkContext = createContext<Props>({
  selectTemplates: () => Promise.resolve(undefined),
  selectLayers: () => Promise.resolve(undefined),
  selectCropSettings: () => Promise.resolve(undefined),
});

export default function ArtworkProvider({
  children,
}: {
  children?: ReactNode;
}) {
  const [selectionType, setSelectionType] = useState<SelectionType>();

  // artwork -----------------------------------------------------------------
  const [selectedArtwork, setSelectedArtwork] = useState<Artwork>();
  const [artworkFilter, setArtworkFilter] = useState<any>({});

  useEffect(() => {
    setSelectedTemplate(undefined);
    if (selectedArtwork) {
      setTemplateFilter((pre: any) => ({
        ...(pre || {}),
        artworkId: selectedArtwork._id,
      }));
    } else {
      setTemplateFilter(undefined);
    }
  }, [selectedArtwork]);

  // template ----------------------------------------------------------------
  const [selectedTemplate, setSelectedTemplate] = useState<Template>();
  const [openTemplatePicker, setOpenTemplatePicker] = useState(false);
  const selectTemplatesPromise =
    useRef<(template: Template[] | undefined) => void>(null);
  const [templateFilter, setTemplateFilter] = useState<any>();

  const startSelectingTemplates = async () => {
    setSelectionType(SelectionType.template);
    setOpenTemplatePicker(true);
    return new Promise<Template[] | undefined>((resolve) => {
      selectTemplatesPromise.current = resolve;
    });
  };

  const onSelectTemplates = (templates?: Template[]) => {
    selectTemplatesPromise.current?.(templates);
    setOpenTemplatePicker(false);
  };

  useEffect(() => {
    if (!openTemplatePicker) {
      selectTemplatesPromise.current?.(undefined);
      setSelectionType(undefined);
      setTemplateFilter(undefined);
      setLayerFilter(undefined);
    }
  }, [openTemplatePicker]);

  useEffect(() => {
    if (selectedTemplate) {
      setLayerFilter((pre: any) => ({
        ...pre,
        templateId: selectedTemplate._id,
      }));
    } else {
      setLayerFilter(undefined);
    }
  }, [selectedTemplate]);

  // layers -------------------------------------------------------------------
  const selectLayersPromise =
    useRef<(layer: Layer[] | undefined) => void>(null);
  const [layerFilter, setLayerFilter] = useState<any>();

  const startSelectingLayers = async () => {
    setSelectionType(SelectionType.layer);
    setOpenTemplatePicker(true);
    return new Promise<Layer[] | undefined>((resolve) => {
      selectLayersPromise.current = resolve;
    });
  };

  const onSelectLayers = (layers?: Layer[]) => {
    selectLayersPromise.current?.(layers);
    setOpenTemplatePicker(false);
  };

  // crop settings -----------------------------------------------------------
  const [openCsPicker, setOpenCsPicker] = useState(false);
  const selectCsPromise = useRef<(cs: CropSettings | undefined) => void>(null);

  const startSelectingCs = async () => {
    setOpenCsPicker(true);
    return new Promise<CropSettings | undefined>((resolve) => {
      selectCsPromise.current = resolve;
    });
  };

  const onSelectCs = (cropSettings?: CropSettings) => {
    selectCsPromise.current?.(cropSettings);
    setOpenCsPicker(false);
  };

  useEffect(() => {
    if (!openCsPicker) {
      selectCsPromise.current?.(undefined);
    }
  }, [openCsPicker]);

  return (
    <ArtworkContext.Provider
      value={{
        selectTemplates: startSelectingTemplates,
        selectLayers: startSelectingLayers,
        selectCropSettings: startSelectingCs,
      }}
    >
      {/* Template ------------------------------------------------------*/}
      <Dialog
        header={Header[selectionType || ""]}
        visible={openTemplatePicker}
        className="dialog"
        style={{ width: "70vw", height: "70vh" }}
        headerStyle={{ padding: 16 }}
        contentClassName="border-t-1 border-gray-300"
        contentStyle={{ padding: 0 }}
        onHide={() => setOpenTemplatePicker(false)}
      >
        <div className="flex h-full overflow-auto">
          <ArtworkManagement
            header="Artworks"
            filter={artworkFilter}
            onChange={setSelectedArtwork}
          />
          <div className="h-full w-[1px] bg-gray-300"></div>
          <TemplateManagement
            header="Templates"
            filter={templateFilter}
            onChange={setSelectedTemplate}
            onSelect={
              selectionType === SelectionType.template
                ? onSelectTemplates
                : undefined
            }
          />
          {selectionType === SelectionType.layer && (
            <>
              <div className="h-full w-[1px] bg-gray-300"></div>
              <LayerManagement
                header="Layers"
                filter={layerFilter}
                onSelect={
                  selectionType === SelectionType.layer
                    ? onSelectLayers
                    : undefined
                }
              />
            </>
          )}
        </div>
      </Dialog>

      {/* Crop settings ------------------------------------------------------*/}
      <Dialog
        header="Crop settings"
        visible={openCsPicker}
        className="dialog"
        style={{ width: "70vw", height: "70vh" }}
        headerStyle={{ padding: 16 }}
        contentClassName="border-t-1 border-gray-300"
        contentStyle={{ padding: 0 }}
        onHide={() => setOpenCsPicker(false)}
      >
        <div className="flex h-full overflow-auto">
          <CropSettingsManagement onSelect={onSelectCs} />
        </div>
      </Dialog>

      {children}
    </ArtworkContext.Provider>
  );
}

export const useArtwork = () => useContext(ArtworkContext);
